import { api } from '@api/api';
import {
  AddWarrantyClaimAttachmentRequest,
  AddWarrantyClaimAttachmentResponse,
  BCProductsResponse,
  DeleteWarrantyClaimAttachmentRequest,
  WarrantyClaimStatus,
  WarrantyCreateFormResponse,
  WarrantyCreateFromRequest,
  WarrantyDetailsResponse,
  WarrantyListRequest,
  WarrantyListResponse,
  WarrantyUpdateFormRequest,
  WarrantyUpdateStatusRequest,
  WarrantyValidQuantityResponse,
  WarrantyVerifySerialNumberRequest,
  WarrantyVerifySerialNumberResponse,
} from '@api/types';
import { ProductFilterOptions } from './types/product-filters/ProductFilters';

export const warrantyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    verifySerialNumber: builder.query<
      WarrantyVerifySerialNumberResponse,
      WarrantyVerifySerialNumberRequest
    >({
      query: (params) => ({
        url: `/api/serial-number/verify?serialNumber=${params.serialNumber}&id=${params.id}`,
        method: 'GET',
      }),
    }),

    getWarrantyClaimDetails: builder.query<WarrantyDetailsResponse, string>({
      query: (params) => ({
        url: `/api/warranty-claims/${params}/details`,
        method: 'GET',
      }),
    }),

    getWarrantyClaimList: builder.query<WarrantyListResponse, WarrantyListRequest>({
      query: (params) => ({
        url: `/api/warranty-claims/list`,
        method: 'POST',
        body: params,
      }),
    }),

    addWarrantyClaim: builder.mutation<WarrantyCreateFormResponse, WarrantyCreateFromRequest>({
      query: (params) => ({
        url: `/api/warranty-claims/add`,
        method: 'POST',
        body: params,
      }),
    }),

    updateWarrantyClaimForm: builder.mutation<number, WarrantyUpdateFormRequest>({
      query: (formData) => ({
        url: `/api/warranty-claims/update`,
        method: 'PUT',
        body: formData,
      }),
    }),

    updateWarrantyClaimStatus: builder.mutation<WarrantyClaimStatus, WarrantyUpdateStatusRequest>({
      query: ({ newStatus, warrantyClaimId }) => ({
        url: `/api/warranty-claims/${warrantyClaimId}/update-status?newStatus=${newStatus}`,
        method: 'PATCH',
        body: {
          newStatus,
        },
      }),
    }),

    addWarrantyClaimAttachments: builder.mutation<
      AddWarrantyClaimAttachmentResponse,
      AddWarrantyClaimAttachmentRequest
    >({
      query: ({ warrantyClaimId, formData }) => ({
        url: `/api/warranty-claims/${warrantyClaimId}/attachment/add`,
        method: 'POST',
        body: formData,
      }),
    }),

    deleteWarrantyClaimAttachment: builder.mutation<void, DeleteWarrantyClaimAttachmentRequest>({
      query: ({ warrantyClaimId, attachmentId }) => ({
        url: `/api/warranty-claims/${warrantyClaimId}/attachment/${attachmentId}/delete`,
        method: 'DELETE',
      }),
    }),

    getProductNoValidQuantity: builder.query<WarrantyValidQuantityResponse, string>({
      query: (productNo) => ({
        url: `/api/warranty-claims/valid-quantity/${productNo}`,
        method: 'GET',
      }),
    }),

    getWarrantyClaimsProductFilters: builder.query<ProductFilterOptions, void>({
      query: () => ({
        url: `/api/warranty-claims/productFilters`,
        method: 'GET',
      }),
    }),
    // approveWarrantyClaim: builder.mutation<void, number | undefined>({
    //   query: (warrantyClaimId) => ({
    //     url: `/api/warranty-claims/${warrantyClaimId}/approve`,
    //     method: 'PATCH',
    //   }),
    // }),
    getNewWarrantyClaimsCount: builder.query<number, void>({
      query: () => ({
        url: `/api/warranty-claims/new-claims-count`,
      }),
    }),
    getBcProducts: builder.query<BCProductsResponse, void>({
      query: () => ({
        url: `/api/warranty-claims/bc-products`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useAddWarrantyClaimMutation,
  useGetWarrantyClaimListQuery,
  useGetWarrantyClaimDetailsQuery,
  useLazyGetWarrantyClaimDetailsQuery,
  useVerifySerialNumberQuery,
  useLazyVerifySerialNumberQuery,
  useUpdateWarrantyClaimFormMutation,
  useLazyGetWarrantyClaimListQuery,
  useAddWarrantyClaimAttachmentsMutation,
  useDeleteWarrantyClaimAttachmentMutation,
  useLazyGetProductNoValidQuantityQuery,
  useGetWarrantyClaimsProductFiltersQuery,
  useGetNewWarrantyClaimsCountQuery,
  useLazyGetNewWarrantyClaimsCountQuery,
  useGetBcProductsQuery,
  useLazyGetBcProductsQuery,
} = warrantyApi;
